import React, { useState, useEffect } from 'react';
import './ClueTracker.css';

const ClueTracker = ({ teamNumber = 1, opponentTeamNumber = 2, isLightTheme = false }) => {
  // New state to track if viewing opponent's tracker
  const [viewingOpponent, setViewingOpponent] = useState(false);
  // Track animation state for smooth transitions
  const [animating, setAnimating] = useState(false);
  
  // Debug logging
  useEffect(() => {
    console.log("ClueTracker props:", { teamNumber, opponentTeamNumber, isLightTheme });
    console.log("Current view:", viewingOpponent ? "Opponent" : "Own team");
    console.log("Applied theme:", getThemeClass());
  }, [teamNumber, opponentTeamNumber, isLightTheme, viewingOpponent]);
  
  // Load initial state from localStorage if available
  const loadStoredValues = (isOpponent = false) => {
    try {
      const key = isOpponent ? `decryptoClueTracker_team${opponentTeamNumber}` : `decryptoClueTracker_team${teamNumber}`;
      const storedValues = localStorage.getItem(key);
      if (storedValues) {
        const parsedValues = JSON.parse(storedValues);
        // Ensure we always have exactly 8 items in the upper grid, regardless of what was stored
        const upperGrid = Array(8).fill('');
        
        // Copy values from stored data (handles both old data with 6 items and new data with 8)
        if (parsedValues.upperGrid && parsedValues.upperGrid.length > 0) {
          parsedValues.upperGrid.forEach((value, index) => {
            if (index < 8) upperGrid[index] = value;
          });
        }
        
        return {
          upperGrid,
          lowerGrid: parsedValues.lowerGrid || Array(4).fill('')
        };
      }
    } catch (e) {
      console.error('Error loading stored clue tracker values:', e);
    }
    // Default values if no stored values or error
    return {
      upperGrid: Array(8).fill(''),
      lowerGrid: Array(4).fill('')
    };
  };

  // Separate state for player and opponent trackers
  const [playerGridValues, setPlayerGridValues] = useState(loadStoredValues(false));
  const [opponentGridValues, setOpponentGridValues] = useState(loadStoredValues(true));
  
  // Combined getter for current grid values based on viewingOpponent state
  const gridValues = viewingOpponent ? opponentGridValues : playerGridValues;
  const setGridValues = viewingOpponent ? setOpponentGridValues : setPlayerGridValues;

  // Verify array sizes on component mount and fix if necessary
  useEffect(() => {
    // Ensure player grid has correct structure
    if (!playerGridValues.upperGrid || playerGridValues.upperGrid.length !== 8) {
      console.log("Fixing player grid structure - expected 8 items, found:", playerGridValues.upperGrid?.length);
      const fixedPlayerGrid = {
        upperGrid: Array(8).fill(''),
        lowerGrid: playerGridValues.lowerGrid || Array(4).fill('')
      };
      setPlayerGridValues(fixedPlayerGrid);
    }
    
    // Ensure opponent grid has correct structure
    if (!opponentGridValues.upperGrid || opponentGridValues.upperGrid.length !== 8) {
      console.log("Fixing opponent grid structure - expected 8 items, found:", opponentGridValues.upperGrid?.length);
      const fixedOpponentGrid = {
        upperGrid: Array(8).fill(''),
        lowerGrid: opponentGridValues.lowerGrid || Array(4).fill('')
      };
      setOpponentGridValues(fixedOpponentGrid);
    }
  }, []);

  // Save to localStorage whenever values change
  useEffect(() => {
    try {
      localStorage.setItem(`decryptoClueTracker_team${teamNumber}`, JSON.stringify(playerGridValues));
    } catch (e) {
      console.error('Error saving player clue tracker values:', e);
    }
  }, [playerGridValues, teamNumber]);
  
  useEffect(() => {
    try {
      localStorage.setItem(`decryptoClueTracker_team${opponentTeamNumber}`, JSON.stringify(opponentGridValues));
    } catch (e) {
      console.error('Error saving opponent clue tracker values:', e);
    }
  }, [opponentGridValues, opponentTeamNumber]);

  // Handle changes in text areas
  const handleUpperGridChange = (index, value) => {
    const newUpperGrid = [...gridValues.upperGrid];
    newUpperGrid[index] = value;
    setGridValues({ ...gridValues, upperGrid: newUpperGrid });
  };

  const handleLowerGridChange = (index, value) => {
    const newLowerGrid = [...gridValues.lowerGrid];
    newLowerGrid[index] = value;
    setGridValues({ ...gridValues, lowerGrid: newLowerGrid });
  };

  // Clear all text areas
  const handleClearAll = () => {
    setGridValues({
      upperGrid: Array(8).fill(''),
      lowerGrid: Array(4).fill('')
    });
  };
  
  // Toggle between player's and opponent's tracker with animation
  const toggleOpponentView = () => {
    if (animating) return; // Prevent multiple rapid toggles
    
    setAnimating(true);
    setTimeout(() => {
      setViewingOpponent(prev => !prev);
      // Add a slight delay before removing the animation class for smoother transition
      setTimeout(() => {
        setAnimating(false);
      }, 300);
    }, 150);
  };
  
  // Determine correct theme class based on viewing state and overall game theme
  const getThemeClass = () => {
    // If viewing opponent, we need to switch themes
    if (viewingOpponent) {
      // If current user is Team 2 (light theme), apply explicit dark theme class
      if (isLightTheme) {
        return 'dark-theme';
      }
      // If current user is Team 1 (dark theme), apply light theme class
      return 'light-theme';
    }
    
    // Not viewing opponent, so keep the default theme (no class for dark, light-theme already applied from parent)
    return '';
  };

  return (
    <div 
      className={`clue-tracker ${viewingOpponent ? 'opponent-clue-tracker' : ''} ${getThemeClass()} ${animating ? 'animating' : ''}`}
      data-team={teamNumber}
      data-opponent={opponentTeamNumber}
      data-theme={isLightTheme ? 'light' : 'dark'}
      data-viewing={viewingOpponent ? 'opponent' : 'own'}
    >
      {viewingOpponent && (
        <div className="opponent-theme-indicator">
          Team {opponentTeamNumber} View
        </div>
      )}
      
      <div className="clue-tracker-header">
        <h4>
          {viewingOpponent 
            ? `Team ${opponentTeamNumber} Clue Tracker` 
            : `Team ${teamNumber} Clue Tracker`}
        </h4>
        <div>
          <button 
            className={`toggle-opponent-button ${viewingOpponent ? 'viewing-opponent' : ''}`} 
            onClick={toggleOpponentView}
            disabled={animating}
          >
            {viewingOpponent 
              ? `View Team ${teamNumber} Tracker` 
              : `View Team ${opponentTeamNumber} Tracker`}
          </button>
          <button className="clear-button" onClick={handleClearAll} disabled={animating}>
            Clear All
          </button>
        </div>
      </div>
      
      {/* Upper grid - 4 rows x 2 columns */}
      <div className="upper-grid">
        {gridValues.upperGrid.map((value, index) => (
          <div key={`upper-${index}`} className="grid-cell">
            <textarea
              value={value}
              onChange={(e) => handleUpperGridChange(index, e.target.value)}
              placeholder={viewingOpponent 
                ? `Team ${opponentTeamNumber} clue notes` 
                : `Team ${teamNumber} clue notes`}
              className="grid-textarea"
              disabled={animating}
            />
          </div>
        ))}
      </div>
      
      {/* Lower grid - 1 row x 4 columns */}
      <div className="lower-grid">
        {gridValues.lowerGrid.map((value, index) => (
          <div key={`lower-${index}`} className="grid-cell">
            <div className="number-label">{index + 1}</div>
            <textarea
              value={value}
              onChange={(e) => handleLowerGridChange(index, e.target.value)}
              placeholder={viewingOpponent 
                ? `Team ${opponentTeamNumber} word ${index + 1}` 
                : `Team ${teamNumber} word ${index + 1}`}
              className="grid-textarea"
              disabled={animating}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ClueTracker; 